<template>
  <section class="hand-rankings-settings settings">
    <header>
      <h1 class="page-header">Hand rankings</h1>
    </header>
    <div v-if="shortDeck" class="radio">
      <label>
        <input v-model="handRankingsType" type="radio" value="short-deck-all-in-5-handed">
        Short deck all in 5-handed
      </label>
      (<a href="/static/short-deck-all-in-five-handed-hand-rankings.txt">order</a>)
    </div>
    <div v-if="shortDeck" class="radio">
      <label>
        <input v-model="handRankingsType" type="radio" value="short-deck-all-in-heads-up">
        Short deck all in heads up
      </label>
      (<a href="/static/short-deck-all-in-heads-up-hand-rankings.txt">order</a>)
      <hr>
    </div>
    <div class="radio">
      <label>
        <input v-model="handRankingsType" type="radio" value="ten-handed-evolutionary">
        10-handed evolutionary
      </label>
      (<a href="http://www.propokertools.com/orderings/heordering.txt">order</a>)
    </div>
    <div class="radio">
      <label>
        <input v-model="handRankingsType" type="radio" value="six-handed-evolutionary">
        6-handed evolutionary
      </label>
      (<a href="http://www.propokertools.com/orderings/he6maxordering.txt">order</a>)
    </div>
    <div class="radio">
      <label>
        <input v-model="handRankingsType" type="radio" value="three-handed-evolutionary">
        3-handed evolutionary
      </label>
      (<a href="http://www.propokertools.com/orderings/he3maxordering.txt">order</a>)
    </div>
    <div class="radio">
      <label>
        <input v-model="handRankingsType" type="radio" value="expected-value">
        Expected value
      </label>
      (<a href="https://www.tightpoker.com/poker_hands.html">order</a>)
    </div>
    <div class="radio">
      <label>
        <input v-model="handRankingsType" type="radio" value="sklansky-chubukov">
        Sklansky-Chubukov
      </label>
      (<a href="http://archives2.twoplustwo.com/showflat.php?Cat=0&Number=382772&page=3&fpart=all&vc=1">order</a>)
    </div>
    <div class="radio">
      <label>
        <input v-model="handRankingsType" type="radio" value="all-in-9-handed">
        All in 9-handed
      </label>
      (<a href="https://www.gamblingsites.org/poker/texas-holdem/starting-hand-rankings/">order</a>)
    </div>
    <div class="radio">
      <label>
        <input v-model="handRankingsType" type="radio" value="all-in-3-handed">
        All in 3-handed
      </label>
      (<a href="https://wizardofodds.com/games/texas-hold-em/3-player-game/">order</a>)
    </div>
    <div class="radio">
      <label>
        <input v-model="handRankingsType" type="radio" value="all-in-heads-up">
        All in heads up
      </label>
      (<a href="http://www.propokertools.com/orderings/hevsrandomordering.txt">order</a>)
    </div>
    <div class="radio">
      <label>
        <input v-model="handRankingsType" type="radio" value="heads-up-equity-squared">
        Heads up equity squared
      </label>
      (<a href="http://www.propokertools.com/orderings/heequitysquaredordering.txt">order</a>)
    </div>
    <div class="radio">
      <label>
        <input v-model="handRankingsType" type="radio" value="custom">
        Custom:
      </label>
      <input ref="customHandRankings" v-bind:value="$store.state.account.user.settings.general.handRankings.customHandRankings" type="text" v-on:click="handRankingsType = 'custom'">
    </div>
    <hr>
    <section class="buttons">
      <button class="btn btn-default btn-lg" v-on:click="isHelpVisible = !isHelpVisible">
        <span v-if="!isHelpVisible">Show help <span class="caret-down"></span></span>
        <span v-if="isHelpVisible">Hide help <span class="caret-up"></span></span>
      </button>
      <button class="btn btn-primary btn-lg" v-on:click="saveHandRankings()">Save</button>
    </section>
    <section v-if="isHelpVisible" class="well">
      <h3>What do you mean by "hand rankings?"</h3>
      <p>When you slide the slider, it adds/removes hands in a certain order. This order is based on how the hands are ranked.</p>
      <p>For example, if you have the hands ranked in the following order: AA, KK, QQ, JJ, AKs, TT..., then when you slide the slider, AA gets added first, then KK, then QQ, then JJ, then AKs, then TT, etc.</p>

      <h3>There is no definitive way to rank starting poker hands</h3>
      <p>The strength of a starting hands depends on many things. For example, whether the pot is heads up or multi-way:</p>
      <ul>
        <li>When playing a multi-way pot, hands like 98s have a relatively higher value, because in a multi-way pot you usually need a premium hand to win, and 98s is good for making flushes and straights.</li>
        <li>When playing heads up, a hand like A8o has a relatively higher value because it makes solid top pairs, and it gives you a good kicker when no one makes a pair or better. But in a multi-way pot, A8o has a relatively low value because it has a relatively low chance of making premium hands such as three of a kind, straights, and flushes.</li>
      </ul>
      <p>The strength of a starting hand also depends on other things like your table image, your perceived range, your position, your opponents range, and your opponents playing style.</p>

      <h3>Descriptions</h3>
      <p><strong>X-handed evolutionary</strong>: Uses an evolutionary computer simulation where hands are simulated against other "good hands". See <a href="http://pokercoder.blogspot.co.uk/2006/07/towards-meaningful-ordering-of-hands.html">Towards a Meaningful Ordering of Hands</a>.</p>
      <p><strong>Expected value</strong>: Uses actual expected values compiled from live table data.</p>
      <p><strong>Sklansky-Chubukov</strong>: Compares hands based on how they perform in a particular situation where you shove from the SB. See <a href="https://poker.stackexchange.com/a/1377/4911">this</a> StackExchange answer for more.</p>
      <p><strong>All in x-handed</strong>: Eg. suppose you have AA and you are all in vs. 9 random opponents. Run a lot of simulations to see how often you win. Then try with KK, QQ, etc. Rank the hands by win percentage. Ditto for x random opponents.</p>
      <p><strong>Heads up equity squared</strong>: Pre-flop equity squared vs. a random hand.</p>
      <p><strong>Custom</strong>: Enter your own customized hand rankings. It should look something like this: aa, kk, aks, ako, etc.</p>
      <ul>
        <li>Make sure you enter all 169 hands</li>
        <li>Use a comma to separate hands</li>
        <li>It doesn't matter whether or not there is a space after the commas</li>
        <li>No abbreviations like JJ+</li>
        <li>Upper/lowercase doesn't matter</li>
        <li>Valid pair hand example: aa</li>
        <li>Valid suited hand example: aks</li>
        <li>Valid offsuit hand example: ako</li>
      </ul>
    </section>
  </section>
</template>

<script>
import settingsValidators from '@/services/settings-validators';

export default {
  name: 'hand-rankings-settings',
  data: function () {
    return {
      handRankingsType: this.$store.state.account.user.settings.general.handRankings.type,
      isHelpVisible: false,
    }
  },
  computed: {
    shortDeck: function () {
      return this.$store.getters['account/purchasedShortDeck'];
    },
  },
  methods: {
    saveHandRankings: function () {
      let self = this;
      let customHandRankings = '';
      let user = _.cloneDeep(this.$store.state.account.user);

      if (this.handRankingsType === 'custom') {
        customHandRankings = this.$refs.customHandRankings.value;

        if (!settingsValidators.areCustomHandRankingsValid(customHandRankings)) {
          alert('The custom hands you entered are invalid. See the rules at the bottom of this page.');
        } else {
          customHandRankings = customHandRankings.toLowerCase();
          customHandRankings = customHandRankings.replace(/, /g, ',');
        }
      }

      user.settings.general.handRankings = {
        type: self.handRankingsType,
        customHandRankings: customHandRankings,
      },
      self.$http.put(`${process.env.BASE_API_URL}/user`, { user: user }).then(function (res) {
        self.$store.commit('account/setUser', res.data.user);
        alert('Saved!');
      }).catch(function () {
        alert('Unable to save settings.');
      });
    }
  },
};
</script>

<style lang="scss" scoped>
  @import 'src/assets/settings';
</style>



// WEBPACK FOOTER //
// src/components/settings/general/hand-rankings.vue